import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InterviewInviteService } from 'src/services/api/InterviewInviteService';

// `scope` is either `{ token }` (magic link) or `{ secMappingId }` (opened
// from the authenticated "My Applications" list).
const isProtected = (scope) => scope?.secMappingId != null;
const scopeKey = (scope) =>
  isProtected(scope) ? `pid:${scope.secMappingId}` : scope?.token;
const inviteKey = (scope) => ['interviewInvite', scopeKey(scope)];

/**
 * Interview invite info for one signed link token (or protected sec_mapping_id).
 * Drives which state the page renders (locked / live / done / cancelled / …).
 * Disabled without a scope and not retried, so an invalid/expired token or a
 * pipeline that isn't the candidate's surfaces the invalid-link panel
 * immediately.
 */
export function useInterviewInvite(scope = {}) {
  return useQuery({
    queryKey: inviteKey(scope),
    queryFn: () =>
      isProtected(scope)
        ? InterviewInviteService.getInfoProtected(scope.secMappingId)
        : InterviewInviteService.getInfo(scope.token),
    enabled: !!(scope.token || scope.secMappingId),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Confirm (is_accepted: true) or decline (false) attendance. Re-fetches the
 * invite on success so the UI re-derives from the fresh server state rather than
 * guessing locally.
 */
export function useAcknowledgeInterview(scope = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isAccepted) =>
      isProtected(scope)
        ? InterviewInviteService.acknowledgeProtected(
            scope.secMappingId,
            isAccepted
          )
        : InterviewInviteService.acknowledge(scope.token, isAccepted),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: inviteKey(scope) }),
  });
}

export default useInterviewInvite;
